import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Editor } from "@tiptap/core";
import { cn } from '@/lib/utils'
import { getStepAction, getImageUrl } from "@deepguide-ai/shared";
import {
  ZoomInIcon,
  ZoomOutIcon,
  Trash2Icon,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { Button } from "@deepguide-ai/ui";
import { Slider } from "@deepguide-ai/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "./plate-ui/dropdown-menu";
import { Toolbar, ToolbarButton } from "./plate-ui/toolbar";
// import { trpc } from '../../../../../web/src/lib/trpc'
// import { useTopicAtomsContext } from '../../../../../web/src/hooks/useTopicAtomsContext'
import { InteractionPoint, ScreenshotAttributes } from "./screenshot-extension";
import { InteractionPointsManager } from "./plate-ui/interaction-point";
import { Resizable } from "./resizable";
import { useScreenshotStepData } from "@/tiptap/hooks/useExtensionData";
import { ScreenshotRenderer } from "./shared/screenshot-renderer";

// Helper to check for prop changes, useful for memoization
const propsAreEqual = (prevProps: any, nextProps: any) => {
  const propsEqual =
    prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.className === nextProps.className &&
    prevProps.zoomLevel === nextProps.zoomLevel &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.screenshotSize.width === nextProps.screenshotSize.width &&
    prevProps.screenshotSize.height === nextProps.screenshotSize.height &&
    JSON.stringify(prevProps.elementBox) ===
      JSON.stringify(nextProps.elementBox) &&
    JSON.stringify(prevProps.interactionPoints) ===
      JSON.stringify(nextProps.interactionPoints) &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);
  return propsEqual;
};

// Toolbar Component (adapted from screenshot-element-toolbar.tsx)
const ScreenshotNodeViewToolbar = ({
  zoomLevel,
  onZoomChange,
  onAlignChange,
  align,
  onDelete,
  readOnly,
}: {
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  onAlignChange: (align: "left" | "center" | "right") => void;
  align: "left" | "center" | "right";
  onDelete: () => void;
  readOnly: boolean;
}) => {
  const alignItems = [
    { icon: AlignLeft, value: "left" },
    { icon: AlignCenter, value: "center" },
    { icon: AlignRight, value: "right" },
  ];
  const IconValue =
    alignItems.find((item) => item.value === align)?.icon ?? AlignCenter;

  return (
    <Toolbar
      variant="media"
      className={cn(
        "absolute top-2 right-2 h-7 z-10 opacity-0 transition-opacity group-hover:opacity-100 border-none",
        readOnly && "hidden"
      )}
      contentEditable={false}
    >
      {/* Zoom Controls */}
      <ToolbarButton
        size="none"
        variant="media"
        onClick={() => onZoomChange(Math.max(1, zoomLevel - 0.25))}
        tooltip="Zoom out"
        disabled={zoomLevel <= 1}
        className={cn(
          "hover:bg-white/10 p-0.5",
          zoomLevel <= 1 && "opacity-50"
        )}
      >
        <ZoomOutIcon className="size-4" />
      </ToolbarButton>
      <div className="flex items-center px-2">
        <Slider
          value={[zoomLevel]}
          max={10}
          min={1}
          step={0.25}
          onValueChange={(value) => onZoomChange(value[0])}
          className="w-12 [&_[data-slot=slider-track]]:h-1 [&_[data-slot=slider-track]]:bg-white/30 [&_[data-slot=slider-range]]:bg-white [&_[data-slot=slider-thumb]]:h-3 [&_[data-slot=slider-thumb]]:w-3 [&_[data-slot=slider-thumb]]:border-0 [&_[data-slot=slider-thumb]]:bg-white [&_[data-slot=slider-thumb]]:shadow-sm"
        />
      </div>
      <div className="flex w-8 items-center justify-center px-1 text-xs text-white">
        {zoomLevel.toFixed(1)}x
      </div>
      <ToolbarButton
        size="none"
        variant="media"
        onClick={() => onZoomChange(Math.min(10, zoomLevel + 0.25))}
        tooltip="Zoom in"
        disabled={zoomLevel >= 10}
        className={cn(
          "hover:bg-white/10 p-0.5",
          zoomLevel >= 10 && "opacity-50"
        )}
      >
        <ZoomInIcon className="size-4" />
      </ToolbarButton>

      {/* Alignment Dropdown */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <ToolbarButton
            size="none"
            variant="media"
            className="hover:bg-white/10 p-0.5"
            tooltip="Align"
          >
            <IconValue className="size-4" />
          </ToolbarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-0 rounded-md border-none bg-black/60 p-0 shadow-none"
          align="end"
        >
          <DropdownMenuRadioGroup
            className="flex hover:bg-transparent"
            value={align}
            onValueChange={(value) =>
              onAlignChange(value as "left" | "center" | "right")
            }
          >
            {alignItems.map(({ icon: Icon, value: itemValue }) => (
              <DropdownMenuRadioItem
                key={itemValue}
                className="size-[26px] opacity-60 hover:opacity-100 text-white data-[state=checked]:bg-black/5 data-[state=checked]:opacity-100"
                value={itemValue}
                hideIcon
              >
                <Icon className="size-4 w-4 h-4 text-white" />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Button */}
      <ToolbarButton
        size="none"
        variant="media"
        onClick={onDelete}
        tooltip="Remove screenshot"
        className="hover:bg-red-500/20 p-0.5 hover:text-red-400"
      >
        <Trash2Icon className="size-4" />
      </ToolbarButton>
    </Toolbar>
  );
};

// Main NodeView Component
export const ScreenshotNodeView = ({
  node,
  editor,
  updateAttributes,
  deleteNode,
  selected,
}: {
  node: ProseMirrorNode;
  editor: Editor;
  getPos: () => number | undefined;
  updateAttributes: (attributes: Partial<ScreenshotAttributes>) => void;
  deleteNode: () => void;
  selected: boolean;
}) => {
  const attrs = node.attrs as ScreenshotAttributes;
  const {
    url,
    step_id,
    width,
    height,
    zoomLevel = 1,
    align = "center",
    elementBox,
    interactionPoints: serializedInteractionPoints,
    selectedTopicId,
  } = attrs;

  // const { isGenerationMode, generationStatus } = useTopicAtomsContext()
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable states to prevent flickering during streaming
  const [stableUrl, setStableUrl] = useState(url || '');
  const [stableInteractionPoints, setStableInteractionPoints] = useState<InteractionPoint[]>([]);
  const [stableWidth, setStableWidth] = useState(width || 800);
  const [stableHeight, setStableHeight] = useState(height || 600);
  const [stableElementBox, setStableElementBox] = useState(elementBox);
  // Update stable states only when we get valid values
  useEffect(() => {
    if (url && url !== stableUrl) {
      setStableUrl(url);
    }
    if (width && width !== stableWidth) {
      setStableWidth(width);
    }
    if (height && height !== stableHeight) {
      setStableHeight(height);
    }
    if (elementBox && elementBox !== stableElementBox) {
      setStableElementBox(elementBox);
    }
  }, [url, stableUrl, width, stableWidth, height, stableHeight, elementBox, stableElementBox]);
  // gjnN4CBlNTHx50jJqAEeo
  // Only fetch when step_id looks like a DB ID (avoid human-friendly IDs like "step-save-todo")
  const isLikelyDbId = !!step_id && (step_id.length === 21 || step_id.startsWith('step-')) // && /^[a-z0-9\\-_]{21,}$/.test(step_id);
  const shouldFetchStep = !!step_id && isLikelyDbId && !stableUrl;
  
  // Use the useScreenshotStepData hook to get step data
  // Use global step resolution since selectedTopicId isn't necessary for screenshots
  const { step, isLoading: isLoadingStep } = useScreenshotStepData({
    stepId: step_id || undefined,
    enabled: shouldFetchStep,
  });

  // Hydrate attributes from fetched step data
  useEffect(() => {
    if (step && !stableUrl) {
      const newAttrs: Partial<ScreenshotAttributes> = {};

      if (step.screenshot?.url) {
        newAttrs.url = step.screenshot.url;
        newAttrs.width = step.screenshot.width ?? 800;
        newAttrs.height = step.screenshot.height ?? 600;
        setStableWidth(step.screenshot.width ?? 800);
        setStableHeight(step.screenshot.height ?? 600);
      }
      if (step.viewportCoordinates) {
        newAttrs.elementBox = step.viewportCoordinates;
        setStableElementBox(step.viewportCoordinates);
      }
      if (step.viewportCoordinates && step.viewportInfo) {
        const interactionPoints = [
          {
            id: step.id,
            type: step.action || "Click",
            x: step.viewportCoordinates.center.x,
            y: step.viewportCoordinates.center.y,
            width: step.viewportCoordinates.width,
            height: step.viewportCoordinates.height,
            xPercent:
              (step.viewportCoordinates.center.x / step.viewportInfo.width) *
              100,
            yPercent:
              (step.viewportCoordinates.center.y / step.viewportInfo.height) *
              100,
            widthPercent:
              (step.viewportCoordinates.width / step.viewportInfo.width) * 100,
            heightPercent:
              (step.viewportCoordinates.height / step.viewportInfo.height) *
              100,
            label: getStepAction(step) ?? "",
          },
        ];
        newAttrs.interactionPoints = interactionPoints;
        setStableInteractionPoints(interactionPoints);
      }
      if (Object.keys(newAttrs).length > 0) {
        updateAttributes(newAttrs);
      }
    }
  }, [step, stableUrl, updateAttributes]);

  const handleZoomChange = useCallback(
    (newZoomLevel: number) => {
      const clampedZoomLevel = Math.max(1, Math.min(10, newZoomLevel));
      updateAttributes({ zoomLevel: clampedZoomLevel });
    },
    [updateAttributes]
  );

  const handleAlignChange = useCallback(
    (newAlign: "left" | "center" | "right") => {
      updateAttributes({ align: newAlign });
    },
    [updateAttributes]
  );

  const screenshotSize = useMemo(
    () => ({ width: stableWidth, height: stableHeight }),
    [stableWidth, stableHeight]
  );


  const imageStyle = useMemo(() => {
    const transform = `scale(${zoomLevel})`;
    const transformOrigin =
      stableElementBox && stableWidth && stableHeight
        ? `${(stableElementBox.center.x / stableWidth) * 100}% ${(stableElementBox.center.y / stableHeight) * 100}%`
        : "center";

    return {
      objectFit: stableElementBox ? ("cover" as const) : ("contain" as const),
      objectPosition:
        stableElementBox && stableWidth && stableHeight
          ? `${(stableElementBox.center.x / stableWidth) * 100}% ${(stableElementBox.center.y / stableHeight) * 100}%`
          : "center",
      transform,
      transformOrigin,
    };
  }, [stableElementBox, stableWidth, stableHeight, zoomLevel]);

  const interactionPoints =
    serializedInteractionPoints || stableInteractionPoints || [];

  const handleWidthChange = useCallback(
    (newWidth: number) => {
      const newHeight = (newWidth / stableWidth) * stableHeight;
      updateAttributes({ width: newWidth, height: newHeight });
      setStableWidth(newWidth);
      setStableHeight(newHeight);
    },
    [stableWidth, stableHeight, updateAttributes]
  );

  if (!stableUrl) {
    return (
      <NodeViewWrapper
        className={cn(
          "screenshot-container relative my-4 flex",
          {
            "justify-center": align === "center",
            "justify-start": align === "left",
            "justify-end": align === "right",
          },
          selected && "ProseMirror-selectednode"
        )}
      >
        <div
          className="screenshot-placeholder bg-muted/40 border-2 border-dashed border-border rounded-md flex items-center justify-center text-muted-foreground p-8"
          style={{
            width: "100%",
            maxWidth: 800,
            minHeight: 200,
          }}
        >
          <div className="text-center">
            <div className="text-sm font-medium">
              {isLoadingStep ? "Loading Screenshot..." : "No screenshot available"}
            </div>
            {step_id && (
              <div className="text-xs text-muted-foreground mt-1">
                Step ID: {step_id}
              </div>
            )}
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className={cn(
        "screenshot-container relative my-4 flex",
        {
          "justify-center": align === "center",
          "justify-start": align === "left",
          "justify-end": align === "right",
        },
        selected && "ProseMirror-selectednode"
      )}
      data-drag-handle
    >
      <div className="relative">
        <ScreenshotRenderer
          url={stableUrl}
          width={stableWidth}
          height={stableHeight}
          elementBox={stableElementBox}
          interactionPoints={interactionPoints}
          cropStyle={attrs.cropStyle || 'full'}
          align={align}
          alt={attrs.alt || ""}
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          onAlignChange={handleAlignChange}
          onWidthChange={handleWidthChange}
          readOnly={!editor.isEditable}
          isFullScreenshot={true}
        />
        <ScreenshotNodeViewToolbar
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          onAlignChange={handleAlignChange}
          align={align}
          onDelete={deleteNode}
          readOnly={editor.isEditable === false}
        />
      </div>
    </NodeViewWrapper>
  );
};
